import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext.tsx';

// Configure axios instance
const api = axios.create({
    baseURL: 'https://taj.city/backend/api',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add response interceptor
api.interceptors.response.use(
    (response) => {
        // //console.log('API Response:', {
        //     url: response.config.url,
        //     method: response.config.method,
        //     status: response.status,
        //     data: response.data
        // });
        return response;
    },
    (error) => {
        console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        throw error;
    }
);

// Add request interceptor
api.interceptors.request.use(
    (config) => {
        //console.log('API Request:', {
        //     url: config.url,
        //     method: config.method,
        //     data: config.data,
        //     headers: config.headers
        // });
        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

interface FollowContextType {
    followedItems: string[];
    followItem: (itemId: string) => Promise<void>;
    unfollowItem: (itemId: string) => Promise<void>;
    isFollowing: (itemId: string) => boolean;
    refreshFollowedItems: () => Promise<void>;
}

const FollowContext = createContext<FollowContextType | undefined>(undefined);

export const FollowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [followedItems, setFollowedItems] = useState<string[]>([]);
    const { user } = useAuth();

    const refreshFollowedItems = async () => {
        try {
            if (!user) {
                //console.log('No user found, skipping fetch');
                setFollowedItems([]);
                return;
            }
            
            //console.log('Fetching followed items for user:', user.id);
            const response = await fetch(`https://taj.city/backend/api/follow/get_followed_items.php?user_id=${user.id}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            //console.log('Followed items response:', data);
            
            if (Array.isArray(data)) {
                setFollowedItems(data);
            } else if (data.message === "No followed items found.") {
                setFollowedItems([]);
            } else {
                console.error('Invalid response format for followed items:', data);
                setFollowedItems([]);
            }
        } catch (error) {
            console.error('Error fetching followed items:', error);
            setFollowedItems([]);
        }
    };

    useEffect(() => {
        refreshFollowedItems();
    }, [user]);

    const followItem = async (itemId: string) => {
        if (!user) {
            console.error('Cannot follow: No user logged in');
            return;
        }

        try {
            //console.log('Following item:', { itemId, userId: user.id });
            
            const response = await fetch('https://taj.city/backend/api/follow/follow.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    user_id: Number(user.id),
                    item_id: itemId
                })
            });

            //console.log('Follow request sent:', {
            //     url: 'https://taj.city/backend/api/follow/follow.php',
            //     method: 'POST',
            //     body: {
            //         user_id: Number(user.id),
            //         item_id: itemId
            //     }
            // });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            //console.log('Follow response:', data);

            // Refresh the list of followed items
            await refreshFollowedItems();
        } catch (error) {
            console.error('Error following item:', error);
            throw error;
        }
    };

    const unfollowItem = async (itemId: string) => {
        if (!user) {
            console.error('Cannot unfollow: No user logged in');
            return;
        }

        try {
            //console.log('Unfollowing item:', { itemId, userId: user.id });
            
            const response = await fetch('https://taj.city/backend/api/follow/follow.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    user_id: Number(user.id),
                    item_id: itemId
                })
            });

            //console.log('Unfollow request sent:', {
            //     url: 'https://taj.city/backend/api/follow/follow.php',
            //     method: 'POST',
            //     body: {
            //         user_id: Number(user.id),
            //         item_id: itemId
            //     }
            // });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            //console.log('Unfollow response:', data);

            // Refresh the list of followed items
            await refreshFollowedItems();
        } catch (error) {
            console.error('Error unfollowing item:', error);
            throw error;
        }
    };

    const isFollowing = (itemId: string) => {
        return followedItems.includes(itemId);
    };

    return (
        <FollowContext.Provider value={{ 
            followedItems, 
            followItem, 
            unfollowItem, 
            isFollowing,
            refreshFollowedItems 
        }}>
            {children}
        </FollowContext.Provider>
    );
};

export const useFollow = () => {
    const context = useContext(FollowContext);
    if (context === undefined) {
        throw new Error('useFollow must be used within a FollowProvider');
    }
    return context;
}; 